import type { ProjectJob } from '../api/types';
import type { OverviewCoverageRow, OverviewInventorySummary } from '../api/types';
import {
  chartCanonicalColor,
  chartPaletteColor,
  chartStatusColor,
} from './chart-colors';
import { parseUtcDate, TEHRAN_TZ } from './datetime';
import { startOfRange, type StatsRange } from './job-range';

export type ChartSlice = {
  id: string;
  label: string;
  value: number;
  color: string;
  pct: number;
};

export function pctOf(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((value / total) * 1000) / 10;
}

export function coverageLabel(key: string): string {
  if (key === '__never_inspected__') return 'بدون بازرسی';
  if (key === '__unknown__') return 'نامشخص';
  return key;
}

export function colorForBucket(bucket: string, index = 0): string {
  if (bucket === 'indexed') return chartStatusColor('indexed');
  if (bucket === 'excluded') return chartStatusColor('excluded');
  if (bucket === 'error') return chartStatusColor('error');
  if (bucket === 'unknown') return chartStatusColor('unknown');
  return chartPaletteColor(index);
}

export function inventoryDonutSlices(
  summary: OverviewInventorySummary,
  options?: { neverLabel?: string },
): ChartSlice[] {
  const inspected = Math.max(0, summary.total - summary.neverInspected);
  const base = inspected > 0 ? inspected : summary.total;
  const slices: ChartSlice[] = [
    {
      id: 'indexed',
      label: 'ایندکس‌شده',
      value: summary.indexed,
      color: chartStatusColor('indexed'),
      pct: pctOf(summary.indexed, base),
    },
    {
      id: 'notIndexed',
      label: 'ایندکس‌نیست',
      value: summary.notIndexed,
      color: chartStatusColor('excluded'),
      pct: pctOf(summary.notIndexed, base),
    },
    {
      id: 'error',
      label: 'خطا',
      value: summary.error,
      color: chartStatusColor('error'),
      pct: pctOf(summary.error, base),
    },
  ];
  if (summary.neverInspected > 0) {
    slices.push({
      id: 'never',
      label: options?.neverLabel ?? 'بدون بازرسی',
      value: summary.neverInspected,
      color: chartStatusColor('never'),
      pct: pctOf(summary.neverInspected, summary.total),
    });
  }
  return slices.filter((item) => item.value > 0);
}

export function coverageTableRows(
  rows: OverviewCoverageRow[],
  total: number,
  options?: { neverLabel?: string },
): ChartSlice[] {
  return rows
    .filter((row) => row.count > 0)
    .map((row, index) => ({
      id: row.key,
      label:
        row.key === '__never_inspected__' && options?.neverLabel
          ? options.neverLabel
          : coverageLabel(row.key),
      value: row.count,
      color: colorForBucket(row.bucket, index),
      pct: pctOf(row.count, total),
    }));
}

export function tehranDayKey(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TEHRAN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function tehranDayLabel(key: string): string {
  const date = new Date(`${key}T12:00:00+03:30`);
  return new Intl.DateTimeFormat('fa-IR', {
    timeZone: TEHRAN_TZ,
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function dayKeysForRange(days: StatsRange): string[] {
  const end = startOfRange(1);
  const keys: string[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - i);
    keys.push(tehranDayKey(d));
  }
  return keys;
}

export type DailyIndexPoint = {
  day: string;
  label: string;
  success: number;
  failed: number;
};

export type DailyInspectPoint = {
  day: string;
  label: string;
  indexed: number;
  notIndexed: number;
  error: number;
};

export function aggregateIndexJobsByDay(
  jobs: ProjectJob[],
  days: StatsRange,
): DailyIndexPoint[] {
  const keys = dayKeysForRange(days);
  const map = new Map(
    keys.map((day) => [
      day,
      { day, label: tehranDayLabel(day), success: 0, failed: 0 },
    ]),
  );
  const from = startOfRange(days).getTime();

  for (const job of jobs) {
    if (job.kind !== 'google_index' && job.kind !== 'indexnow') continue;
    const ts = parseUtcDate(job.createdAt).getTime();
    if (ts < from) continue;
    const key = tehranDayKey(parseUtcDate(job.createdAt));
    const row = map.get(key);
    if (!row) continue;
    row.success += job.successCount;
    row.failed += job.failedCount;
  }

  return keys.map((key) => map.get(key)!);
}

export function aggregateInspectJobsByDay(
  jobs: ProjectJob[],
  days: StatsRange,
): DailyInspectPoint[] {
  const keys = dayKeysForRange(days);
  const map = new Map(
    keys.map((day) => [
      day,
      { day, label: tehranDayLabel(day), indexed: 0, notIndexed: 0, error: 0 },
    ]),
  );
  const from = startOfRange(days).getTime();

  for (const job of jobs) {
    if (job.kind !== 'inspect') continue;
    const ts = parseUtcDate(job.createdAt).getTime();
    if (ts < from) continue;
    const key = tehranDayKey(parseUtcDate(job.createdAt));
    const row = map.get(key);
    if (!row) continue;
    const summary = job.indexStatusSummary;
    if (summary) {
      row.indexed += summary.indexed;
      row.notIndexed += summary.notIndexed;
    } else {
      row.error += job.failedCount;
    }
  }

  return keys.map((key) => map.get(key)!);
}

export function changesDonutSlices(
  summary: {
    becameIndexed: number;
    becameNotIndexed: number;
    error: number;
    other: number;
  },
): ChartSlice[] {
  const total =
    summary.becameIndexed +
    summary.becameNotIndexed +
    summary.error +
    summary.other;
  const slices: ChartSlice[] = [
    {
      id: 'becameIndexed',
      label: 'ایندکس شد',
      value: summary.becameIndexed,
      color: chartStatusColor('indexed'),
      pct: pctOf(summary.becameIndexed, total),
    },
    {
      id: 'becameNotIndexed',
      label: 'ایندکس از دست رفت',
      value: summary.becameNotIndexed,
      color: chartStatusColor('excluded'),
      pct: pctOf(summary.becameNotIndexed, total),
    },
    {
      id: 'error',
      label: 'خطا',
      value: summary.error,
      color: chartStatusColor('error'),
      pct: pctOf(summary.error, total),
    },
    {
      id: 'other',
      label: 'سایر',
      value: summary.other,
      color: chartStatusColor('other'),
      pct: pctOf(summary.other, total),
    },
  ];
  return slices.filter((item) => item.value > 0);
}

const CANONICAL_LABELS: Record<string, string> = {
  submitted_indexed: 'Submitted and indexed',
  alternate_canonical: 'Alternate page with proper canonical tag',
  duplicate_google_canonical: 'Duplicate, Google chose different canonical',
  duplicate_no_user_canonical: 'Duplicate without user-selected canonical',
  other: 'سایر',
  __never_inspected__: 'بدون بازرسی',
};

export function canonicalLabel(key: string): string {
  return CANONICAL_LABELS[key] ?? key;
}

export function noindexDonutSlices(
  summary: {
    noindex: number;
    indexingAllowed: number;
  },
): ChartSlice[] {
  const total = summary.noindex + summary.indexingAllowed;
  return [
    {
      id: 'allowed',
      label: 'Indexing allowed',
      value: summary.indexingAllowed,
      color: chartStatusColor('success'),
      pct: pctOf(summary.indexingAllowed, total),
    },
    {
      id: 'noindex',
      label: "Noindex — Meta 'robots'",
      value: summary.noindex,
      color: chartStatusColor('failed'),
      pct: pctOf(summary.noindex, total),
    },
  ].filter((item) => item.value > 0);
}

export function canonicalDonutSlices(
  rows: { key: string; count: number }[],
): ChartSlice[] {
  const inspected = rows.filter((row) => row.key !== '__never_inspected__');
  const total = inspected.reduce((sum, row) => sum + row.count, 0);
  return inspected
    .filter((row) => row.count > 0)
    .map((row, index) => ({
      id: row.key,
      label: canonicalLabel(row.key),
      value: row.count,
      color: chartCanonicalColor(row.key, index),
      pct: pctOf(row.count, total),
    }));
}

export function crawlFreshnessSlices(
  summary: {
    within30Days: number;
    older30Days: number;
    crawlUnknown: number;
    neverInspected: number;
  },
): ChartSlice[] {
  const total =
    summary.within30Days +
    summary.older30Days +
    summary.crawlUnknown +
    summary.neverInspected;
  return [
    {
      id: 'within30',
      label: 'کرول کمتر از ۳۰ روز',
      value: summary.within30Days,
      color: chartStatusColor('indexed'),
      pct: pctOf(summary.within30Days, total),
    },
    {
      id: 'older30',
      label: 'کرول بیش از ۳۰ روز',
      value: summary.older30Days,
      color: chartStatusColor('excluded'),
      pct: pctOf(summary.older30Days, total),
    },
    {
      id: 'unknown',
      label: 'بدون تاریخ کرول',
      value: summary.crawlUnknown,
      color: chartStatusColor('unknown'),
      pct: pctOf(summary.crawlUnknown, total),
    },
    {
      id: 'never',
      label: 'بدون بازرسی',
      value: summary.neverInspected,
      color: chartStatusColor('never'),
      pct: pctOf(summary.neverInspected, total),
    },
  ].filter((item) => item.value > 0);
}

export function jobRequestOutcomeSlices(jobs: ProjectJob[]): ChartSlice[] {
  let completed = 0;
  let failed = 0;
  let partial = 0;
  let cancelled = 0;
  for (const job of jobs) {
    if (job.state === 'completed') completed += 1;
    else if (job.state === 'failed') failed += 1;
    else if (job.state === 'partial') partial += 1;
    else if (job.state === 'cancelled') cancelled += 1;
  }
  const total = completed + failed + partial + cancelled;
  return [
    {
      id: 'completed',
      label: 'درخواست موفق',
      value: completed,
      color: chartStatusColor('success'),
      pct: pctOf(completed, total),
    },
    {
      id: 'partial',
      label: 'بخشی موفق',
      value: partial,
      color: chartStatusColor('partial'),
      pct: pctOf(partial, total),
    },
    {
      id: 'failed',
      label: 'درخواست ناموفق',
      value: failed,
      color: chartStatusColor('failed'),
      pct: pctOf(failed, total),
    },
    {
      id: 'cancelled',
      label: 'متوقف‌شده',
      value: cancelled,
      color: chartStatusColor('cancelled'),
      pct: pctOf(cancelled, total),
    },
  ].filter((item) => item.value > 0);
}

export function indexUrlOutcomeSlices(jobs: ProjectJob[]): ChartSlice[] {
  let success = 0;
  let failed = 0;
  for (const job of jobs) {
    if (job.kind !== 'google_index' && job.kind !== 'indexnow') continue;
    success += job.successCount;
    failed += job.failedCount;
  }
  const total = success + failed;
  return [
    {
      id: 'success',
      label: 'آدرس موفق',
      value: success,
      color: chartStatusColor('success'),
      pct: pctOf(success, total),
    },
    {
      id: 'failed',
      label: 'آدرس ناموفق',
      value: failed,
      color: chartStatusColor('failed'),
      pct: pctOf(failed, total),
    },
  ].filter((item) => item.value > 0);
}

export function canonicalTableRows(
  rows: { key: string; count: number }[],
  total: number,
): ChartSlice[] {
  return rows
    .filter((row) => row.count > 0 && row.key !== '__never_inspected__')
    .map((row, index) => ({
      id: row.key,
      label: canonicalLabel(row.key),
      value: row.count,
      color: chartCanonicalColor(row.key, index),
      pct: pctOf(row.count, total),
    }));
}
